import { customElement } from 'lit/decorators.js';
import { TuftsGrid } from '../tufts-grid/tufts-grid';
import { CSSResultGroup } from 'lit';
import componentStylesGrid from '../tufts-grid/tufts-grid.css.lit';
import componentStylesEqual from '../tufts-grid/tufts-grid-equal.css.lit';
import { debounce } from '../../../utilities/debounce';
import { ResizeController } from '../../controllers/resize-controller';
import componentStyles from './tufts-grid-sophisticated.css.lit';

/**
 * The tufts grid component
 * @element tufts-grid-sophisticated
 *
 * @attr row-gap
 * @attr col-gap
 * @attr columns
 * @attr item-min-width
 * @attr align-self
 * @attr justify-self
 * @attr equal-height
 * @attr span-first
 */

@customElement('tufts-grid-sophisticated')
export class TuftsGridSophisticated extends TuftsGrid {
  static styles: CSSResultGroup = [
    componentStyles,
    componentStylesGrid,
    componentStylesEqual,
  ];

  resizeObserver: ResizeObserver;
  maxColumnsOriginal: string;
  // Create a single instance of the debounced function outside of the ResizeObserver
  // to ensure debounce is not called multiple times unnecessarily
  debouncedAdjustGridColumns = debounce(this.adjustGridColumns.bind(this), 200);

  // The resizeController is initialized here because attributes are not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    // Store the original maximum number of columns
    this.maxColumnsOriginal = this.columns;

    if (this.spanFirst) {
      // Calculate the minimum width of two columns in pixels
      const minWidthOf2Columns =
        this.convertRemToPx(this.itemMinWidth) * 2 +
        this.convertRemToPx(this.colGap);

      // Initialize the ResizeController with the minimum width of two columns as a breakpoint
      this.resizeController = new ResizeController(this, {
        breakpoints: [minWidthOf2Columns - 1],
      });
    }
    // Initialize ResizeObserver with debounced adjustGridColumns
    this.resizeObserver = new ResizeObserver(this.debouncedAdjustGridColumns);
    this.resizeObserver.observe(this);
    // Initial call to set columns
    this.adjustGridColumns();
  }
  convertToPx(value: string, containerWidth: number): number {
    let result: number;
    if (value.endsWith('rem')) {
      result = parseFloat(value) * 16;
    } else if (value.endsWith('%')) {
      result = (parseFloat(value) / 100) * containerWidth;
    } else {
      result = parseInt(value, 10);
    }
    if (Number.isNaN(result)) {
      console.warn(`Invalid value "${value}" provided to convertToPx.`);
      result = 0; // Default to 0 or another sensible default
    }
    return result;
  }

  adjustGridColumns() {
    const containerWidth = this.offsetWidth;
    const minItemWidth = this.convertToPx(this.itemMinWidth, containerWidth);
    const colGap = this.convertToPx(this.colGap, containerWidth);
    // Calculate the number of items that can fit
    const maxItems = Math.floor(containerWidth / (minItemWidth + colGap));
    let maxColumns = parseInt(this.maxColumnsOriginal, 10);
    if (Number.isNaN(maxColumns) || maxColumns <= 0) {
      console.warn(
        `Invalid maxColumnsOriginal value: "${this.maxColumnsOriginal}"`
      );
      // Default to 3 or another appropriate value
      maxColumns = 3;
    }

    // Determine the number of columns based on the calculated maxItems
    let columns;
    if (maxItems >= maxColumns) {
      columns = maxColumns;
    } else if (maxItems >= 2) {
      columns = 2;
    } else {
      columns = 1;
    }

    // Update the CSS variable for grid-template-columns
    this.columns = columns.toString();
  }

  convertRemToPx(item: string) {
    if (item.endsWith('rem')) {
      return parseFloat(item) * 16;
    } else {
      return parseInt(item, 10);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-grid-sophisticated': TuftsGridSophisticated;
  }
}
